import React, { memo, useCallback, useState } from 'react'

import { HiMiniCheck, HiMiniXMark } from 'react-icons/hi2'

import Button from '@/components/common/Button'
import IconButton from '@/components/common/IconButtonV2'
import PopoverV2 from '@/components/common/PopoverV2'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import { ColorIndicator } from '@/components/tags/ColorIndicator'

import type { UUID } from '@/types/Id'
import type { Organization } from '@/types/Organization'
import type { PursuitStage } from '@/types/pursuit/Stage'

const onOpenAutoFocus = (event: Event) => {
  event.preventDefault()
}

const StageSelectionCheck: React.FC<{ checked: boolean }> = memo(
  ({ checked }) => (
    <span
      aria-hidden="true"
      className={
        checked
          ? 'flex h-4 w-4 shrink-0 items-center justify-center rounded border border-accent-400 bg-accent-400 text-white'
          : 'h-4 w-4 shrink-0 rounded border border-primary-300 bg-primary-100 dark:border-primary-600 dark:bg-primary-700'
      }
    >
      {checked ? <HiMiniCheck size={14} /> : null}
    </span>
  ),
)

StageSelectionCheck.displayName = 'StageSelectionCheck'

interface StageNameGroup {
  name: string
  color: PursuitStage['color']
  index: number
  stageIds: UUID[]
  organizations: Organization[]
}

interface PipelineStageSelectionPopoverProps {
  selectedStageIds: UUID[]
  setSelectedStageIds: (ids: UUID[]) => void
  stageStatistics: { allStageIds: UUID[]; allSelected: boolean }
  stages: PursuitStage[]
  organizationsById?: Record<UUID, Organization>
  children: React.ReactNode
}

const buildStageNameGroups = (
  stages: PursuitStage[],
  organizationsById?: Record<UUID, Organization>,
): StageNameGroup[] => {
  const groupsByName = new Map<
    string,
    {
      name: string
      color: PursuitStage['color']
      index: number
      stageIds: UUID[]
      organizationIds: Set<UUID>
    }
  >()

  for (const stage of stages) {
    const group = groupsByName.get(stage.name)
    if (group) {
      group.stageIds.push(stage.id)
      group.organizationIds.add(stage.organization_id)
      if (stage.index < group.index) {
        group.index = stage.index
        group.color = stage.color
      }
    } else {
      groupsByName.set(stage.name, {
        name: stage.name,
        color: stage.color,
        index: stage.index,
        stageIds: [stage.id],
        organizationIds: new Set([stage.organization_id]),
      })
    }
  }

  return Array.from(groupsByName.values())
    .map((group) => ({
      name: group.name,
      color: group.color,
      index: group.index,
      stageIds: group.stageIds,
      organizations: Array.from(group.organizationIds)
        .map((id) => organizationsById?.[id])
        .filter((org): org is Organization => Boolean(org))
        .sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.index - b.index || a.name.localeCompare(b.name))
}

export const PipelineStageSelectionPopover: React.FC<PipelineStageSelectionPopoverProps> =
  memo(
    ({
      selectedStageIds,
      setSelectedStageIds,
      stageStatistics,
      stages,
      organizationsById,
      children,
    }) => {
      const [isOpen, setIsOpen] = useState(false)
      const stageGroups = buildStageNameGroups(stages, organizationsById)
      const showOrganizations = Boolean(organizationsById)
      const selectedStageIdSet = new Set(selectedStageIds)
      const selectedStageCount = stageStatistics.allStageIds.filter((id) =>
        selectedStageIdSet.has(id),
      ).length
      const selectedGroupCount = stageGroups.filter((group) =>
        group.stageIds.every((id) => selectedStageIdSet.has(id)),
      ).length

      const handleStageToggle = useCallback(
        (stageIds: UUID[]) => (event: React.MouseEvent<HTMLButtonElement>) => {
          event.preventDefault()
          event.stopPropagation()

          const stageIdSet = new Set(stageIds)
          const selectedIds = new Set(selectedStageIds)
          const isSelected = stageIds.some((id) => selectedIds.has(id))
          if (isSelected) {
            setSelectedStageIds(
              selectedStageIds.filter((id) => !stageIdSet.has(id)),
            )
          } else {
            const next = new Set(selectedStageIds)
            for (const id of stageIds) next.add(id)
            setSelectedStageIds(Array.from(next))
          }
        },
        [selectedStageIds, setSelectedStageIds],
      )

      const handleSelectAll = useCallback(() => {
        setSelectedStageIds(stageStatistics.allStageIds)
      }, [setSelectedStageIds, stageStatistics.allStageIds])

      const handleClear = useCallback(() => {
        setSelectedStageIds([])
      }, [setSelectedStageIds])

      const handleClose = useCallback(() => {
        setIsOpen(false)
      }, [])

      return (
        <PopoverV2 open={isOpen} onOpenChange={setIsOpen}>
          <PopoverV2.Trigger asChild>{children}</PopoverV2.Trigger>
          <PopoverV2.Content
            align="start"
            collisionPadding={16}
            className="w-80 max-h-[min(calc(100vh-4rem),420px)] overflow-hidden p-0"
            portal={true}
            onOpenAutoFocus={onOpenAutoFocus}
          >
            <div className="flex max-h-[min(calc(100vh-4rem),420px)] flex-col">
              <div className="flex items-start justify-between gap-2 p-3">
                <div className="space-y-1 flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-primary-900 dark:text-primary-100">
                    Pipeline Stage Selection
                  </h3>
                  <p className="text-xs text-primary-600 dark:text-primary-400">
                    Configure which pursuit stages are included in your
                    estimated value calculation. This is specific to you.
                    Pursuits marked as no bid are excluded automatically.
                  </p>
                </div>
                <IconButton
                  onClick={handleClose}
                  tooltip="Close"
                  tooltipProps={{ delayDuration: 100 }}
                  className="-mt-1 -mr-1"
                >
                  <HiMiniXMark size={16} />
                </IconButton>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto border-y border-primary-200 dark:border-primary-800 divide-y divide-primary-200 dark:divide-primary-800">
                {stageGroups.map((group) => {
                  const selectedCount = group.stageIds.filter((id) =>
                    selectedStageIdSet.has(id),
                  ).length
                  const isSelected = selectedCount === group.stageIds.length
                  const orgCount = group.organizations.length
                  return (
                    <button
                      key={group.name}
                      type="button"
                      className="flex items-center justify-between gap-x-2 py-2 px-3 w-full transition duration-100 hover:bg-primary-100 dark:hover:bg-primary-800/70"
                      onClick={handleStageToggle(group.stageIds)}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <ColorIndicator
                          color={group.color}
                          className="w-[10px] h-[10px] flex-shrink-0"
                        />
                        <span className="text-left text-sm text-primary-900 dark:text-primary-100 truncate">
                          {group.name}
                        </span>
                      </div>
                      {showOrganizations && orgCount > 0 && (
                        <Tooltip delayDuration={150}>
                          <TooltipTrigger asChild>
                            <span className="shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium text-primary-500 hover:bg-primary-100 hover:text-primary-700 dark:text-primary-400 dark:hover:bg-primary-800 dark:hover:text-primary-200">
                              {orgCount} {orgCount === 1 ? 'org' : 'orgs'}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent
                            side="right"
                            align="center"
                            className="max-w-[220px] py-2"
                            sideOffset={36}
                          >
                            <div className="flex flex-col gap-1">
                              <span className="text-[11px] font-semibold uppercase tracking-wide opacity-70">
                                Applied to
                              </span>
                              {group.organizations.map((organization) => (
                                <span
                                  key={organization.id}
                                  className="truncate text-xs"
                                >
                                  {organization.name}
                                </span>
                              ))}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      )}
                      <StageSelectionCheck checked={isSelected} />
                    </button>
                  )
                })}
              </div>
              <div className="flex items-center justify-between gap-2 p-3">
                <span className="min-w-0 truncate text-xs text-primary-500 dark:text-primary-400">
                  {selectedGroupCount} of {stageGroups.length} stage
                  {stageGroups.length === 1 ? '' : 's'} selected
                </span>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button
                    variant="ghost-compact"
                    onClick={handleClear}
                    disabled={selectedStageCount === 0}
                    className="h-7 px-2"
                  >
                    Clear
                  </Button>
                  <Button
                    variant="outline-compact"
                    onClick={handleSelectAll}
                    disabled={stageStatistics.allSelected}
                    className="h-7 px-2"
                  >
                    Select all
                  </Button>
                </div>
              </div>
            </div>
          </PopoverV2.Content>
        </PopoverV2>
      )
    },
  )

PipelineStageSelectionPopover.displayName = 'PipelineStageSelectionPopover'
